import React, { useState } from "react";
import { Gift, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { api } from "../../lib/api";
import { getSessionId, track } from "../../lib/track";
import { useT } from "../../lib/i18n";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Lightweight, dismissible first-order 10% email offer. The backend is the
// source of truth for eligibility and for applying the discount at checkout.
export default function FirstOrderOffer({ open, onClose, onApplied, kitQty }) {
  const { t } = useT();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const dismiss = () => {
    track("first_order_offer_dismissed");
    onClose();
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const value = email.trim().toLowerCase();
    if (!EMAIL_RE.test(value)) {
      setError(t("fo_invalid_email"));
      return;
    }
    setBusy(true);
    setError("");
    track("first_order_email_submitted");
    try {
      const r = await api.post("/first-order-offer", { session_id: getSessionId(), email: value, kit_qty: kitQty || null });
      if (r.data?.eligible) {
        track("first_order_discount_applied", { meta: { pct: r.data.pct || 10 } });
        onApplied({ type: "first_order", email: value, pct: r.data.pct || 10 });
      } else {
        setError(r.data?.message || t("fo_not_eligible"));
      }
    } catch (err) {
      setError(err?.response?.data?.detail || t("fo_invalid_email"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent className="max-w-sm rounded-[1.3rem] p-0 overflow-hidden" data-testid="first-order-offer-modal">
        <div className="px-6 pt-7 pb-6">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full" style={{ background: "hsl(var(--brand-blush))" }}>
            <Gift className="h-6 w-6" style={{ color: "hsl(var(--brand-rose))" }} />
          </div>
          <DialogHeader className="mt-3 space-y-1 text-center">
            <DialogTitle className="font-display text-xl font-semibold" style={{ color: "hsl(var(--brand-brown))" }}>{t("fo_title")}</DialogTitle>
            <DialogDescription className="text-sm text-[hsl(var(--muted-foreground))]">{t("fo_subtitle")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} noValidate className="mt-4 space-y-2">
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              placeholder={t("fo_email_placeholder")}
              className="h-12 rounded-xl text-base"
              data-testid="first-order-email-input"
              aria-invalid={!!error}
            />
            {error && <p className="text-xs font-medium" style={{ color: "hsl(var(--destructive))" }} data-testid="first-order-error">{error}</p>}
            <Button type="submit" disabled={busy} className="h-12 w-full rounded-xl text-base" data-testid="first-order-submit">
              {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : null}{t("fo_cta")}
            </Button>
          </form>
          <button type="button" onClick={dismiss} className="mt-3 w-full text-center text-sm text-[hsl(var(--muted-foreground))] underline underline-offset-2 hover:text-[hsl(var(--brand-brown))]" data-testid="first-order-dismiss">
            {t("fo_no_thanks")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
